import React, { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * BackToTop — bouton flottant "↑ Haut de page" discret sur mobile.
 *
 * Apparition : après 2000 px de scroll (équivalent ~3 écrans mobile).
 * Position : au-dessus de la BottomNav, à droite. Respecte safe-area iOS.
 * Visuel : cercle 44x44 (touch target AA), fond card/90 + backdrop-blur,
 *          bordure subtile, icône chevron haut. Masqué automatiquement
 *          sur desktop (lg+) car inutile (scroll plus fluide + moins long).
 * A11y : aria-label explicite, `title`, focus-visible ring natif, supports
 *        `prefers-reduced-motion`.
 */
export function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const SHOW_AT = 2000;
    const onScroll = () => {
      setVisible(window.scrollY > SHOW_AT);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = () => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Revenir en haut de page"
      title="Haut de page"
      data-testid="back-to-top-btn"
      className={[
        // Base
        'fixed right-3 z-[44] lg:hidden',
        'w-11 h-11 rounded-full',
        'inline-flex items-center justify-center',
        'bg-card/85 backdrop-blur-md border border-border text-foreground/80',
        'shadow-[0_10px_24px_-10px_rgba(0,0,0,0.35)]',
        'hover:bg-card hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]/40',
        'active:scale-95',
        'transition-[opacity,transform,background-color,color,border-color] duration-200 ease-out',
        // Positionné au-dessus de la BottomNav (72 px) avec un peu d'air (+ safe-area iOS)
        // On utilise bottom via style inline pour combiner avec env() fiable
        visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none',
      ].join(' ')}
      style={{
        bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <ArrowUp className="w-5 h-5" aria-hidden="true" strokeWidth={2.2} />
    </button>
  );
}

export default BackToTop;
